import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Bell, BellOff, Droplets, Pill, Calendar, Sun, Moon, Sparkles, CheckCircle2 } from 'lucide-react';
import notificationService from '../services/notificationService';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';

const NotificationSettings = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsResubscription, setNeedsResubscription] = useState(false);
  const [resubscriptionReason, setResubscriptionReason] = useState('');
  const [settings, setSettings] = useState({
    morningGreeting: true,
    eveningSummary: true,
    waterReminders: true,
    waterInterval: 2,
    medicineReminders: true,
    appointmentReminders: true,
    taskReminders: true,
    motivationBoosts: true,
    workoutReminders: true
  });

  useEffect(() => {
    if (isOpen) {
      // Load settings first, then check push status
      // This ensures backend "enabled" state is loaded before any overrides
      loadSettingsAndStatus().then(() => {
        checkPushSubscriptionStatus();
      });
    }
  }, [isOpen]);

  const checkPushSubscriptionStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/push/status`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Only show refresh banner for STALE subscriptions (expired keys, etc.)
        // NOT for missing subscriptions - that's normal on iOS Safari
        const hasStaleSubscription = data.needs_resubscription && 
          data.reason && 
          data.reason !== 'no_subscription' &&
          data.reason !== 'not_found';
        
        setNeedsResubscription(hasStaleSubscription);
        setResubscriptionReason(data.reason || '');
        
        console.log('🔔 Push status check:', {
          subscribed: data.subscribed,
          needsResubscription: hasStaleSubscription,
          reason: data.reason
        });
      }
    } catch (error) {
      console.error('Error checking push status:', error);
    }
  };

  const loadSettingsAndStatus = async () => {
    try {
      const perm = notificationService.getPermission();
      setPermission(perm);
      
      // CRITICAL: Check if user has ACTUAL push subscription in database
      // This is the ONLY source of truth - not browser permission, not localStorage
      console.log('🔔 Checking actual push subscription status from backend...');
      
      try {
        const pushStatusResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/push/status`,
          { credentials: 'include' }
        );
        
        if (pushStatusResponse.ok) {
          const pushStatus = await pushStatusResponse.json();
          console.log('🔔 Push status from backend:', pushStatus);
          
          // ONLY show as subscribed if there's a REAL subscription in the database
          if (pushStatus.subscribed === true) {
            setIsSubscribed(true);
            console.log('✅ User has valid push subscription in database');
          } else {
            setIsSubscribed(false);
            console.log('❌ No push subscription in database - showing Enable card');
            // Clear any misleading localStorage
            localStorage.removeItem('notifications_enabled');
          }
          
          // Check for stale subscription that needs refresh
          if (pushStatus.needs_resubscription) {
            setNeedsResubscription(true);
            setResubscriptionReason(pushStatus.reason || 'unknown');
          }
        } else {
          console.log('⚠️ Push status check failed:', pushStatusResponse.status);
          setIsSubscribed(false);
        }
      } catch (statusError) {
        console.error('❌ Error checking push status:', statusError);
        setIsSubscribed(false);
      }
      
      // Load notification preferences (separate from subscription status)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
      setIsSubscribed(false);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    console.log('🔔 NotificationSettings: Starting notification enable flow...');
    
    try {
      // Check browser support
      if (!('Notification' in window)) {
        toast({
          title: 'Not Supported',
          description: 'Notifications are not supported in your browser.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ Service Worker not supported - basic notifications only');
      }
      
      if (!('PushManager' in window)) {
        console.warn('⚠️ Push notifications not supported - basic notifications only');
      }
      
      // Request permission directly (must be user-initiated)
      console.log('🔔 Requesting permission...');
      let permission;
      try {
        permission = await Notification.requestPermission();
        console.log('🔔 Permission result:', permission);
      } catch (permError) {
        console.error('❌ Permission request failed:', permError);
        toast({
          title: 'Error',
          description: 'Could not request notification permission.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      if (permission === 'denied') {
        toast({
          title: 'Notifications Blocked',
          description: 'Please enable notifications in your browser settings.',
          variant: 'destructive'
        });
        setPermission('denied');
        setLoading(false);
        return;
      }
      
      if (permission !== 'granted') {
        toast({
          title: 'Permission Required',
          description: 'Please allow notifications when prompted.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      console.log('✅ Permission granted');
      setPermission('granted');
      
      // Try to subscribe to push (may fail on some browsers)
      let pushSuccess = false;
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          console.log('🔔 Subscribing to push...');
          await notificationService.subscribeToPush(process.env.REACT_APP_BACKEND_URL);
          pushSuccess = true;
          console.log('✅ Push subscription successful');
        } catch (pushError) {
          // Push subscription failed - this is a CRITICAL error
          console.error('❌ Push subscription failed:', pushError);
          
          // Show clear error to user
          toast({
            title: '❌ Push Setup Failed',
            description: `Could not enable background notifications: ${pushError.message}`,
            variant: 'destructive'
          });
          
          // Don't mark as subscribed if push failed
          setNeedsResubscription(true);
          setLoading(false);
          return; // Exit early - don't mark as enabled
        }
      } else {
        // No Push API support - warn user
        console.warn('⚠️ Push API not available in this browser');
        toast({
          title: '⚠️ Limited Support',
          description: 'Background push notifications not supported in this browser.',
          variant: 'warning'
        });
        setLoading(false);
        return;
      }
      
      // Only reach here if push subscription succeeded
      setIsSubscribed(true);
      setNeedsResubscription(false);
      
      // Sync settings with backend - only set enabled:true if push worked
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            enabled: pushSuccess,
            ...settings
          })
        });
        console.log('✅ Settings synced with backend, enabled:', pushSuccess);
      } catch (syncError) {
        console.warn('⚠️ Settings sync failed:', syncError);
      }
      
      // Show success notification only if push worked
      if (pushSuccess) {
        try {
          new Notification('🔔 Notifications Enabled!', {
            body: 'You will now receive reminders from LifeFlo AI',
            icon: '/logo192.png'
          });
        } catch (testNotifError) {
          console.warn('⚠️ Test notification failed:', testNotifError);
        }
        
        // CRITICAL: Only set localStorage if push actually worked
        localStorage.setItem('notifications_enabled', 'true');
        localStorage.setItem('notification_prompt_dismissed', 'true');
        
        toast({
          title: '🔔 Notifications Enabled!',
          description: 'You will now receive background reminders and updates.'
        });
      }
      
    } catch (error) {
      console.error('❌ Error enabling notifications:', error);
      toast({
        title: 'Error',
        description: `Failed to enable notifications: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setLoading(true);
    try {
      await notificationService.unsubscribeFromPush(process.env.REACT_APP_BACKEND_URL);
      
      const granted = await notificationService.requestPermission();
      if (granted) {
        await notificationService.subscribeToPush(process.env.REACT_APP_BACKEND_URL);
        setNeedsResubscription(false);
        setIsSubscribed(true);
        
        // Don't send automatic test - show toast only
        toast({
          title: '🔔 Notifications Refreshed!',
          description: 'Your notifications have been updated.'
        });
      }
    } catch (error) {
      console.error('Re-subscription error:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh notifications. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      // Call backend to disable notifications (keeps subscription but disables delivery)
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      // Also unsubscribe from push
      await notificationService.unsubscribeFromPush(process.env.REACT_APP_BACKEND_URL);
      setIsSubscribed(false);
      
      // Update notification settings
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: false })
      });
      
      localStorage.setItem('notifications_enabled', 'false');
      
      toast({
        title: 'Notifications Disabled',
        description: 'You won\'t receive push notifications.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disable notifications',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            ...settings,
            enabled: isSubscribed
          })
        }
      );
      
      if (response.ok) {
        toast({
          title: 'Settings Saved',
          description: 'Your notification preferences have been updated.'
        });
        onClose();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    }
  };

  const notificationTypes = [
    {
      key: 'morningGreeting',
      icon: <Sun className="h-5 w-5 text-yellow-500" />,
      label: t('notifications.morningGreeting'),
      description: t('notifications.morningDesc')
    },
    {
      key: 'eveningSummary',
      icon: <Moon className="h-5 w-5 text-teal-500" />,
      label: t('notifications.eveningSummary'),
      description: t('notifications.eveningDesc')
    },
    {
      key: 'waterReminders',
      icon: <Droplets className="h-5 w-5 text-blue-500" />,
      label: t('notifications.waterReminders'),
      description: t('notifications.waterDesc')
    },
    {
      key: 'medicineReminders',
      icon: <Pill className="h-5 w-5 text-cyan-500" />,
      label: t('notifications.medicineReminders'),
      description: t('notifications.medicineDesc')
    },
    {
      key: 'appointmentReminders',
      icon: <Calendar className="h-5 w-5 text-green-500" />,
      label: t('notifications.appointmentAlerts'),
      description: t('notifications.appointmentDesc')
    },
    {
      key: 'motivationBoosts',
      icon: <Sparkles className="h-5 w-5 text-orange-500" />,
      label: t('notifications.motivationBoosts'),
      description: t('notifications.motivationDesc')
    }
  ];

  // Check if iOS device
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            {t('notifications.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Not Subscribed - Show Enable Card */}
          {!isSubscribed && (
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Bell className="h-8 w-8 text-blue-500" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">
                      Enable Notifications
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Get reminders for water, medicines, tasks, and daily check-ins.
                    </p>
                    
                    {/* iOS warning - only show if not installed as PWA */}
                    {isIOS && !isStandalone && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ Add to Home Screen first for notifications
                      </p>
                    )}
                    
                    {permission === 'denied' && (
                      <p className="text-xs text-red-500 mt-2">
                        ⚠️ Notifications blocked. Enable in browser settings.
                      </p>
                    )}
                    
                    <Button
                      onClick={handleEnableNotifications}
                      disabled={loading || permission === 'denied'}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {loading ? 'Enabling...' : '🔔 Enable'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subscribed - Show Status Card */}
          {isSubscribed && (
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Notifications Active</p>
                      <p className="text-xs text-green-600">Receiving reminders</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDisableNotifications}
                    disabled={loading}
                    className="text-gray-600 border-gray-300"
                  >
                    {loading ? '...' : 'Disable'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Re-subscription Banner - Only when subscription is stale */}
          {needsResubscription && isSubscribed && (
            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Bell className="h-6 w-6 text-amber-600 mt-0.5 animate-pulse" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">Refresh Needed</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your notifications need to be refreshed for reliable delivery.
                    </p>
                    <Button
                      size="sm"
                      onClick={handleResubscribe}
                      disabled={loading}
                      className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {loading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notification Preferences - Only shown when subscribed */}
          {isSubscribed && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">{t('notifications.customize')}</p>
              
              {notificationTypes.map(({ key, icon, label, description }) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {icon}
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-gray-500">{description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings[key]}
                    onCheckedChange={(checked) => setSettings({...settings, [key]: checked})}
                  />
                </div>
              ))}

              {/* Water Reminder Interval */}
              {settings.waterReminders && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Label className="text-sm font-medium">{t('notifications.waterReminderInterval')}</Label>
                  <Select
                    value={String(settings.waterInterval)}
                    onValueChange={(value) => setSettings({...settings, waterInterval: parseInt(value)})}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t('notifications.everyHour')}</SelectItem>
                      <SelectItem value="2">{t('notifications.every2Hours')}</SelectItem>
                      <SelectItem value="3">{t('notifications.every3Hours')}</SelectItem>
                      <SelectItem value="4">{t('notifications.every4Hours')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={handleSaveSettings} className="w-full bg-green-600 hover:bg-green-700">
                {t('notifications.savePreferences')}
              </Button>
              
              {/* Manual Test Notification Button - This is the ONLY way to trigger a backend push from frontend */}
              <Button 
                variant="outline" 
                onClick={async () => {
                  try {
                    toast({ title: '📤 Sending test...', description: 'Please wait...' });
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/test`, {
                      method: 'POST',
                      credentials: 'include'
                    });
                    if (response.ok) {
                      toast({ title: '✅ Test Sent!', description: 'Check for a push notification on your device.' });
                    } else {
                      const data = await response.json().catch(() => ({}));
                      toast({ title: '❌ Test Failed', description: data.detail || 'Could not send test notification.', variant: 'destructive' });
                    }
                  } catch (err) {
                    toast({ title: '❌ Error', description: 'Network error sending test.', variant: 'destructive' });
                  }
                }}
                className="w-full mt-2"
              >
                🔔 {t('notifications.testNotification') || 'Test Notification'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettings;
